import * as React from "react";
import { observer } from "mobx-react";

// imported only for typings
// these are passed to the component from index.ts
import { BaseDict } from "./dict";
import { PersonalizedHighlightsData } from "./data";
import { EventCardDateTime, EventCardIcon, BetBtn, SportHeading, BetBtnTypes, Loader, SportsCarousel, SportsCarouselItem, Icon, EventCardStatisticsIcon } from "sb-resp-ui";

// external components
import * as CMP from "./components";

/*
    Setup testing tools for MobX
    uncoment the <DevTools /> at the end of the PersonalizedHighlights component
*/
// import DevTools from "mobx-react-devtools";
// import { setLogEnabled, setUpdatesEnabled, setGraphEnabled } from "mobx-react-devtools";
// if (process.env.NODE_ENV === "development") {
//     setLogEnabled(true);
//     setUpdatesEnabled(true);
//     setGraphEnabled(true);
// }

@observer
export class PersonalizedHighlights extends React.Component<{
    dataProvider: PersonalizedHighlightsData,
    dict: BaseDict
}, {
    dataProvider: PersonalizedHighlightsData
}> {
    dict: BaseDict

    constructor(props) {
        super(props);

        // setup dictionary
        this.dict = this.props.dict;

        // setup data provider
        this.state = {
            dataProvider: this.props.dataProvider,
        }
    }

    createEventCardLink(eventId: string, modifiers: string) {
        return (
            <a href={this.state.dataProvider.extDataObjects[eventId] ? this.state.dataProvider.extDataObjects[eventId].linkURL : ""}
                onClick={this.state.dataProvider.openGameTranspiler.bind(this.state.dataProvider, eventId)}
                className={modifiers}
                key={`link_${eventId}`}
                data-uat="event-details-wrap">
                <EventCardIcon baseClass="rj-event-card-section-item--game-icon" tooltip="">
                    <Icon i={`rj-event-card-sport-icon i-o-sport-${this.state.dataProvider.events[eventId].sportId}`} />
                </EventCardIcon>
                <div className="rj-event-card-section-item rj-event-card-section-item--league" data-uat="event-details-league-name">
                    {this.state.dataProvider.events[eventId].leagueName}
                </div>
                <div className="rj-event-card-section-item rj-event-card-section-item--team-1-name" data-uat="event-details-team-a-name">
                    {this.state.dataProvider.events[eventId].participant1Name}
                </div>
                <div className="rj-event-card-section-item rj-event-card-section-item--team-2-name" data-uat="event-details-team-b-name">
                    {this.state.dataProvider.events[eventId].participant2Name}
                </div>
                <div className="rj-event-card-section-item rj-event-card-section-item--market-count"  data-uat="event-all-bets-inner">
                    <span className="rj-event-card-section-item-market-count-number">{this.state.dataProvider.events[eventId].marketLinesCount}</span>
                    <span className="rj-event-card-section-item-market-count-arrow"></span>
                </div>
                <EventCardDateTime baseClass="rj-event-card-section-item" content={this.state.dataProvider.events[eventId].eventStartingDate} modifiers="rj-event-card-section-item--date" />
                <EventCardDateTime baseClass="rj-event-card-section-item" content={this.state.dataProvider.events[eventId].eventStartingTime} modifiers="rj-event-card-section-item--time" />
                {this.renderStatisticsIcon(eventId)}
                {this.renderLiveStreamingIcon(eventId)}
            </a>
        )
    }

    createEmptyEventIcon() {
        return (
            <EventCardIcon baseClass="rj-event-card-section-item--event-icon rj-event-card-section-item--hidden-on-mobile" tooltip={""}>
                <Icon i="rj-event-card-event-icon rj-event-card-event-icon--empty i-24-16" />
            </EventCardIcon>
        )
    }

    createEmptyBetButtons() {
        return (
            <div className={`rj-event-card-section rj-event-card-section--3-bet-buttons`} data-uat="bet-buttons-row">
                <BetBtn isDisabled={true} modifier={"rj-event-card-bet-button"} type={BetBtnTypes.ML} text={" "} odd={" "} dataUat={"bet-button"} />
                <BetBtn isDisabled={true} modifier={"rj-event-card-bet-button"} type={BetBtnTypes.ML} text={" "} odd={" "} dataUat={"bet-button"}/>
                <BetBtn isDisabled={true} modifier={"rj-event-card-bet-button"} type={BetBtnTypes.ML} text={" "} odd={" "} dataUat={"bet-button"}/>
            </div>
        );
    }

    createBetButtons(eventId: string) {
        if (!eventId) { return; }
        return (
            <div className={`rj-event-card-section rj-event-card-section--${this.state.dataProvider.markets[this.state.dataProvider.events[eventId].marketIds[0]].selectionIds.length}-bet-buttons`} data-uat="bet-buttons-row">
                {
                    this.state.dataProvider.getSelectionIds(eventId).map(
                        selectionId => {
                            return (
                                <BetBtn
                                    key={selectionId}
                                    isDisabled={this.state.dataProvider.selections[selectionId] ? this.state.dataProvider.selections[selectionId].isDisabled : true}
                                    modifier={this.state.dataProvider.isSelectionInBetslip(selectionId) ?
                                        `rj-event-card-bet-button ${this.state.dataProvider.getUniqueOddsClass(this.state.dataProvider.selections[selectionId].sbTechId, 0)} chosenBet` :
                                        `rj-event-card-bet-button ${this.state.dataProvider.getUniqueOddsClass(this.state.dataProvider.selections[selectionId].sbTechId, 0)}`}
                                    handler={this.state.dataProvider.addToSlipTranspiler.bind(this.state.dataProvider, selectionId)}
                                    type={BetBtnTypes.ML}
                                    text={this.state.dataProvider.selections[selectionId].isDisabled ? " " : this.state.dataProvider.selections[selectionId].displayName}
                                    odd={this.state.dataProvider.selections[selectionId].isDisabled ? " " : this.state.dataProvider.selections[selectionId].odds}
                                    dataUat={"bet-button"} />
                            )
                        }
                    )
                }
            </div>
        )
    }

    renderEventCardLink(eventId: string) {
        return (
            (this.state.dataProvider.selectedSportId === "0")
                ? this.createEventCardLink(eventId, "rj-event-card-section rj-event-card-section-item--event-info")
                : this.createEventCardLink(eventId, "rj-event-card-section rj-event-card-section-item--event-info-without-sport-icon-on-mobile")
        )
    }

    renderLiveStreamingIcon(eventId: string) {
        return (
            (this.state.dataProvider.extDataObjects[eventId] && this.state.dataProvider.extDataObjects[eventId].hasLiveStream) ?
                <EventCardIcon baseClass="rj-event-card-section-item--event-icon" tooltip={this.dict.LiveStreamingTooltip}>
                    <Icon i="rj-event-card-event-icon i-24-16 i-o-ev-streaming" />
                </EventCardIcon> :
                this.createEmptyEventIcon()
        )
    }

    renderStatisticsIcon(eventId: string) {
        return (
            (this.state.dataProvider.extDataObjects[eventId] && this.state.dataProvider.extDataObjects[eventId].hasStatistics) ?
                <EventCardStatisticsIcon modifiers="rj-event-card-section-item--event-icon rj-event-card-section-item--hidden-on-mobile"
                    tooltip={this.dict.StatisticsTooltip}
                    handler={this.state.dataProvider.openStatTranspiler.bind(this.state.dataProvider, eventId)}>
                    <Icon i="rj-event-card-event-icon i-24-16 i-o-ev-statistics" />
                </EventCardStatisticsIcon> :
                this.createEmptyEventIcon()
        )
    }

    renderBetButtons(eventId: string) {
        return (
            (this.state.dataProvider.events[eventId].marketIds.length === 0 || this.state.dataProvider.markets[this.state.dataProvider.events[eventId].marketIds[0]] === undefined)
                ? this.createEmptyBetButtons()
                : this.createBetButtons(eventId)
        )
    }

    render() {
        return (
            <div className="rj-personalized-highlights">
                {(!this.state.dataProvider.isDataLoaded) ? <Loader /> :
                    <div className="rj-personalized-highlights-wrapper">
                        <SportsCarousel>
                            {this.state.dataProvider.sportsSortedById.map((el, index) => {
                                (!this.state.dataProvider.selectedSportId && index === 0)
                                    && this.state.dataProvider.setSelectedSportId(el.id);
                                return (
                                    <SportsCarouselItem
                                        key={el.id}
                                        sportId={el.id}
                                        sportName={el.name}
                                        numberOfEvents={el.eventIds.length}
                                        isSelected={el.id === this.state.dataProvider.selectedSportId}
                                        handler={this.state.dataProvider.setSelectedSportId.bind(this.state.dataProvider, el.id)}
                                    />
                                )
                            })}
                        </SportsCarousel>
                        <SportHeading sportName={this.state.dataProvider.sports[this.state.dataProvider.selectedSportId].name} />
                        <div className="rj-event-cards">
                            {
                                this.state.dataProvider.eventIdsSortedByPersonalizedSortId.map(
                                    eventId => {
                                        return (
                                            <CMP.EventCard key={`${eventId}_root`} eventId={eventId}>
                                                {this.renderEventCardLink(eventId)}
                                                {this.renderBetButtons(eventId)}
                                                <a href={this.state.dataProvider.extDataObjects[eventId] ? this.state.dataProvider.extDataObjects[eventId].linkURL : ""}
                                                    onClick={this.state.dataProvider.openGameTranspiler.bind(this.state.dataProvider, eventId)}
                                                    className="rj-event-card-section rj-event-card-section--market-count">
                                                    <div className="rj-event-card-section-item rj-event-card-section-item--market-count"  data-uat="event-all-bets-inner">
                                                        <span className="rj-event-card-section-item-market-count-number">{this.state.dataProvider.events[eventId].marketLinesCount}</span>
                                                        <span className="rj-event-card-section-item-market-count-arrow"></span>
                                                    </div>
                                                </a>
                                            </CMP.EventCard>
                                        )
                                    }
                                )
                            }
                        </div>
                    </div>
                }
            </div>
        )
    }
}